import React from "react";
import { Link } from "react-router-dom";
import { formatOrganizationEndDate } from "../utils/organizationEndDate";
import { useOrganizationActions } from "../hooks/useOrganizationActions";
import { useEndDateModal } from "../contexts/EndDateModalContext";

const EndDateLine = ({ endDate }) => {
  if (!endDate) return null;
  return (
    <div
      data-testid="company-end-date"
      className={`flex flex-wrap items-center text-xs rounded-lg px-2 py-1 mb-3 ${
        endDate.isPast
          ? "bg-red-50 dark:bg-red-900 text-red-700 dark:text-red-300"
          : "bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
      }`}
    >
      <i className="fas fa-calendar-times mr-2"></i>
      <span>End-date {endDate.label}</span>
      {endDate.reasonLabel && <span className="mx-1">·</span>}
      {endDate.reasonLabel && <span>{endDate.reasonLabel}</span>}
      <span className="mx-1">·</span>
      <span>{endDate.daysLabel}</span>
    </div>
  );
};

const CompanyListItem = ({ company, viewMode = 'card', onChanged }) => {
  const endDate = formatOrganizationEndDate(company);
  const { busy, setStatus, createSupportUser } = useOrganizationActions({ onChanged });
  const { openEndDateModal } = useEndDateModal();

  const isPaused = company.subscriptionStatus === "Paused";
  const toggleStatus = () => setStatus(company, isPaused ? "Active" : "Paused");
  const openEndDate = () => openEndDateModal(company, onChanged);

  // Interim: "Delete" soft-deletes (status = Deleted) pending the Epic C2
  // delete-tenant modal + the hard-vs-soft decision.
  const handleDelete = () => {
    if (window.confirm(`Set ${company.name} to Deleted?`)) {
      setStatus(company, "Deleted");
    }
  };

  const handleSupportClick = () => createSupportUser(company);

  if (viewMode === 'list') {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`p-3 rounded-2xl ${
              company.subscriptionStatus === "Active" 
                ? "bg-grafokett-orange bg-opacity-10 dark:bg-grafokett-orange dark:bg-opacity-20" 
                : company.subscriptionStatus === "Deleted"
                ? "bg-red-50 dark:bg-red-900"
                : "bg-gray-100 dark:bg-gray-700"
            }`}>
              <span className={`text-xl ${
                company.subscriptionStatus === "Active" 
                  ? "text-grafokett-orange" 
                  : company.subscriptionStatus === "Deleted"
                  ? "text-red-600 dark:text-red-400"
                  : "text-gray-600 dark:text-gray-400"
              }`}>
                {company.subscriptionStatus === "Active" ? <span role="img" aria-label="Active company">🏢</span> : 
                 company.subscriptionStatus === "Deleted" ? <span role="img" aria-label="Deleted company">🗑️</span> : <span role="img" aria-label="Paused company">⏸️</span>}
              </span>
            </div>
            
            <div className="flex-1">
              <div className="flex items-center space-x-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {company.name}
                </h3>
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  company.subscriptionStatus === "Active" 
                    ? "bg-grafokett-orange bg-opacity-20 text-grafokett-orange" 
                    : company.subscriptionStatus === "Deleted"
                    ? "bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200"
                    : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                }`}>
                  <div className={`w-2 h-2 rounded-full mr-2 ${
                    company.subscriptionStatus === "Active" 
                      ? "bg-grafokett-orange" 
                      : company.subscriptionStatus === "Deleted"
                      ? "bg-red-500"
                      : "bg-gray-500"
                  }`}></div>
                  {company.subscriptionStatus}
                </div>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-grafokett-blue bg-opacity-10 text-grafokett-blue dark:bg-grafokett-blue dark:bg-opacity-20 dark:text-grafokett-blue">
                  <span role="img" aria-label="Premium subscription">💎</span> {company.subscriptionType || 'Free'}
                </span>
              </div>
              
                      <div className="flex items-center space-x-8 mt-3">
                        <div className="flex items-center bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-600">
                          <i className="fas fa-users text-gray-600 dark:text-gray-400 mr-2"></i>
                          <span className="font-semibold text-gray-900 dark:text-white">{company.maxUsers || 0}</span>
                          <span className="ml-1 text-sm text-gray-600 dark:text-gray-400">Users</span>
                        </div>
                        <div className="flex items-center bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-600">
                          <i className="fas fa-tags text-gray-600 dark:text-gray-400 mr-2"></i>
                          <span className="font-semibold text-gray-900 dark:text-white">{company.maxLabels || 0}</span>
                          <span className="ml-1 text-sm text-gray-600 dark:text-gray-400">Labels</span>
                        </div>
                        <div className="flex items-center bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-600">
                          <i className="fas fa-print text-gray-600 dark:text-gray-400 mr-2"></i>
                          <span className="font-semibold text-gray-900 dark:text-white">{company.maxPrinterModels || 0}</span>
                          <span className="ml-1 text-sm text-gray-600 dark:text-gray-400">Printers</span>
                        </div>
                      </div>
                      {endDate && (
                        <div className="mt-3">
                          <EndDateLine endDate={endDate} />
                        </div>
                      )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Link to={`organization/${company.id}`}>
              <button className="px-4 py-2 bg-grafokett-blue text-white rounded-xl hover:bg-grafokett-blue-dark transition-colors font-medium flex items-center">
                <span className="mr-2" role="img" aria-label="Edit">✏️</span> Edit
              </button>
            </Link>
            
            {company.subscriptionStatus === "Paused" ? (
              <button 
                onClick={toggleStatus}
                className="px-4 py-2 bg-grafokett-orange text-white rounded-xl hover:bg-grafokett-orange-dark transition-colors font-medium flex items-center"
              >
                <span className="mr-2" role="img" aria-label="Activate">▶️</span> Activate
              </button>
            ) : (
              <button 
                onClick={toggleStatus}
                className="px-4 py-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors font-medium flex items-center"
              >
                <span className="mr-2" role="img" aria-label="Pause">⏸️</span> Pause
              </button>
            )}

            <button
              onClick={openEndDate}
              className="px-4 py-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors font-medium flex items-center"
            >
              <span className="mr-2" role="img" aria-label="End-date">📅</span> End-date
            </button>

            <button
              onClick={handleSupportClick}
              disabled={busy}
              className={`px-4 py-2 bg-grafokett-blue bg-opacity-80 text-white rounded-xl transition-colors font-medium flex items-center ${
                busy ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-grafokett-blue'
              }`}
            >
              <span className="mr-2" role="img" aria-label="Support">🆘</span>
              {busy ? 'Creating...' : 'Support'}
            </button>

            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium flex items-center"
            >
              <span className="mr-2" role="img" aria-label="Delete">🗑️</span> Delete
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg hover:shadow-xl transition-all duration-300">
      {/* Header */}
      <div className="p-4 pb-3">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
          {company.name}
        </h3>
        
        {/* Two Column Layout */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          {/* Left Column */}
          <div className="space-y-2">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 border border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <i className="fas fa-users text-gray-600 dark:text-gray-400 mr-1 text-sm"></i>
                  <span className="text-xs text-gray-600 dark:text-gray-400">Users</span>
                </div>
                <div className="text-sm font-bold text-gray-900 dark:text-white">{company.maxUsers || 0}</div>
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 border border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <i className="fas fa-print text-gray-600 dark:text-gray-400 mr-1 text-sm"></i>
                  <span className="text-xs text-gray-600 dark:text-gray-400">Printers</span>
                </div>
                <div className="text-sm font-bold text-gray-900 dark:text-white">{company.maxPrinterModels || 0}</div>
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 border border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <i className="fas fa-chart-bar text-gray-600 dark:text-gray-400 mr-1 text-sm"></i>
                  <span className="text-xs text-gray-600 dark:text-gray-400">Status</span>
                </div>
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  company.subscriptionStatus === "Active" 
                    ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" 
                    : company.subscriptionStatus === "Deleted"
                    ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                    : "bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-300"
                }`}>
                  {company.subscriptionStatus}
                </div>
              </div>
            </div>
          </div>
          
          {/* Right Column */}
          <div className="space-y-2">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 border border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <i className="fas fa-tags text-gray-600 dark:text-gray-400 mr-1 text-sm"></i>
                  <span className="text-xs text-gray-600 dark:text-gray-400">Labels</span>
                </div>
                <div className="text-sm font-bold text-gray-900 dark:text-white">{company.maxLabels || 0}</div>
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 border border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <i className="fas fa-gem text-gray-600 dark:text-gray-400 mr-1 text-sm"></i>
                  <span className="text-xs text-gray-600 dark:text-gray-400">Subscription</span>
                </div>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                  {company.subscriptionType || 'Standard'}
                </span>
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-2 border border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <i className="fas fa-calendar text-gray-600 dark:text-gray-400 mr-1 text-sm"></i>
                  <span className="text-xs text-gray-600 dark:text-gray-400">Created</span>
                </div>
                <div className="text-xs font-semibold text-gray-900 dark:text-white">
                  {company.createdAt ? new Date(company.createdAt).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric' 
                  }) : 'N/A'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* End-date */}
        <EndDateLine endDate={endDate} />

        {/* Action Buttons */}
        <div className="flex items-center space-x-3 pt-3 border-t border-gray-200 dark:border-gray-600">
          <Link to={`organization/${company.id}`}>
            <button className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 text-sm font-medium flex items-center shadow-sm hover:shadow-md">
              <i className="fas fa-edit mr-2"></i>
              Edit
            </button>
          </Link>
          
          {company.subscriptionStatus === "Paused" ? (
            <button 
              onClick={toggleStatus}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 text-sm font-medium flex items-center shadow-sm hover:shadow-md"
            >
              <i className="fas fa-play mr-2"></i>
              Activate
            </button>
          ) : (
            <button 
              onClick={toggleStatus}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 text-sm font-medium flex items-center shadow-sm hover:shadow-md"
            >
              <i className="fas fa-pause mr-2"></i>
              Pause
            </button>
          )}

          <button
            onClick={openEndDate}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 text-sm font-medium flex items-center shadow-sm hover:shadow-md"
          >
            <i className="fas fa-calendar-times mr-2"></i>
            End-date
          </button>

          <button
            onClick={handleSupportClick}
            disabled={busy}
            className={`px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg transition-colors duration-200 text-sm font-medium flex items-center shadow-sm hover:shadow-md ${
              busy ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <i className="fas fa-life-ring mr-2"></i>
            {busy ? 'Creating...' : 'Support'}
          </button>

          <button
            onClick={handleDelete}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 text-sm font-medium flex items-center shadow-sm hover:shadow-md"
          >
            <i className="fas fa-trash mr-2"></i>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanyListItem;
